import { type DraggableItemIdentifier, SortableList } from "@/overlay/components/CommonParts/SortableList";
import type { ThresholdColor } from "@/overlay/components/CommonParts/Threshold/types";
import { Modal } from "@/overlay/components/window/Modal";
import { conditionSymbol } from "@/utility/conditionalExpressions";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button, Input } from "@mm/metx-design-system";
import { tryPrettyPrintUnit } from "@mm/weather-parameter-utils";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import "./CreateAlertModal.scss";

function triggerLabel(parameterUnit: string, threshold: ThresholdColor, displayUnit: string) {
  const symbol = conditionSymbol(threshold.condition);
  const valueWithUnit = displayUnit ? `${threshold.value} ${displayUnit}` : `${threshold.value}`;

  return {
    symbol,
    text: `${parameterUnit} ${symbol} ${valueWithUnit}`,
  };
}

export type AlertLocation = {
  lat?: number | string;
  lon?: number | string;
  name?: string;
  label?: string;
};

type TriggerItem = {
  id: string;
  threshold: ThresholdColor;
  enabled: boolean;
};

type FormValues = {
  title: string;
};

type CreateAlertModalProps = {
  isOpen: boolean;
  onClose: () => void;
  locations: AlertLocation[];
  thresholds: ThresholdColor[];
  parameterUnit: string;
  defaultTitle: string;
  onCreateAlertLayer?: (title: string, thresholds: ThresholdColor[]) => Promise<void>;
};

function createTriggerItems(thresholds: ThresholdColor[]): TriggerItem[] {
  return thresholds.map((threshold, index) => ({
    id: `${index}-${threshold.condition}-${threshold.value}`,
    threshold,
    enabled: true,
  }));
}

export function CreateAlertModal({
  isOpen,
  onClose,
  locations,
  thresholds,
  parameterUnit,
  defaultTitle,
  onCreateAlertLayer,
}: CreateAlertModalProps) {
  const [triggerItems, setTriggerItems] = useState<TriggerItem[]>([]);
  const [isPending, setIsPending] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: defaultTitle,
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset({ title: defaultTitle });
    setTriggerItems(createTriggerItems(thresholds));
    setIsPending(false);
    clearErrors("root.serverError");
  }, [clearErrors, defaultTitle, isOpen, reset, thresholds]);

  const enabledTriggerCount = triggerItems.filter((item) => item.enabled).length;
  const displayUnit = parameterUnit ? tryPrettyPrintUnit(parameterUnit) : "";
  const titleRequiredMessage = t`Please add an alert title.`;
  const createAlertErrorMessage = t`Couldn't create alert subscription. Please try again.`;

  function moveTriggerIdx(dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) {
    setTriggerItems((currentItems) => {
      const copy = [...currentItems];
      const [targetItem] = copy.splice(dragStart.index, 1);
      copy.splice(dropTarget.index, 0, targetItem);
      return copy;
    });
  }

  function toggleTrigger(id: string) {
    setTriggerItems((currentItems) =>
      currentItems.map((item) => (item.id === id ? { ...item, enabled: !item.enabled } : item)),
    );
  }

  const onSubmit = async ({ title }: FormValues) => {
    if (isPending) {
      return;
    }

    if (!onCreateAlertLayer) {
      onClose();
      return;
    }

    setIsPending(true);
    clearErrors("root.serverError");

    try {
      await onCreateAlertLayer(
        title,
        triggerItems.filter((item) => item.enabled).map((item) => item.threshold),
      );
      onClose();
    } catch (error) {
      setError("root.serverError", {
        type: "server",
        message: error instanceof Error && error.message ? error.message : createAlertErrorMessage,
      });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="create-alert-modal">
        <h2 className="create-alert-modal__title">
          <span className="material-icons-outlined" aria-hidden>
            notifications
          </span>
          <Trans>Create Alert subscription</Trans>
        </h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="create-alert-modal__body">
            <Controller
              control={control}
              name="title"
              rules={{
                validate: (value) => value.trim().length > 0 || titleRequiredMessage,
              }}
              render={({ field }) => (
                <Input
                  label={t`Title`}
                  fullWidth
                  placeholder={t`Enter alert title`}
                  value={field.value}
                  onChange={(event) => field.onChange(event.currentTarget.value)}
                />
              )}
            />
            {errors.title?.message && <p className="create-alert-modal__error">{errors.title.message}</p>}

            <div>
              <p className="create-alert-modal__section-label">
                <Trans>Location</Trans>
              </p>
              <div className="create-alert-modal__table-wrap">
                <table className="create-alert-modal__table">
                  <thead>
                    <tr>
                      <th>
                        <Trans>latitude</Trans>
                      </th>
                      <th>
                        <Trans>longitude</Trans>
                      </th>
                      <th>
                        <Trans>name</Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map((location, index) => (
                      <tr key={`${location.lat}-${location.lon}-${index}`}>
                        <td>{location.lat}</td>
                        <td>{location.lon}</td>
                        <td>{location.name ?? location.label ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <p className="create-alert-modal__section-label">
                <Trans>Triggers</Trans> ({enabledTriggerCount})
              </p>
              <SortableList
                className="create-alert-modal__triggers"
                onSwapItems={moveTriggerIdx}
                items={triggerItems.map((item, index) => {
                  const label = triggerLabel(parameterUnit, item.threshold, displayUnit);

                  return {
                    id: item.id,
                    content: (
                      <div
                        className={
                          item.enabled
                            ? "create-alert-modal__trigger"
                            : "create-alert-modal__trigger create-alert-modal__trigger--disabled"
                        }
                      >
                        <span className="material-icons create-alert-modal__drag-icon" aria-hidden>
                          drag_indicator
                        </span>
                        <span className="create-alert-modal__trigger-order">{index + 1}.</span>
                        <input
                          type="checkbox"
                          className="create-alert-modal__trigger-checkbox"
                          checked={item.enabled}
                          onChange={() => toggleTrigger(item.id)}
                          aria-label={label.text}
                        />
                        <span className="create-alert-modal__trigger-label">
                          {parameterUnit} {label.symbol} <strong>{item.threshold.value}</strong>
                          {displayUnit ? ` ${displayUnit}` : null}
                        </span>
                      </div>
                    ),
                  };
                })}
              />
            </div>
          </div>
          {errors.root?.serverError?.message && (
            <p className="create-alert-modal__error">{errors.root.serverError.message}</p>
          )}
          <div className="create-alert-modal__footer">
            <Button variant="secondary" type="button" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={enabledTriggerCount === 0 || isPending}
              loading={isPending}
            >
              <Trans>Create Alert</Trans>
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
